import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Loader2, Sparkles, Home, UserPlus, Check, Calendar } from "lucide-react";
import { siteAIChat, siteAILead, siteAIBook, fmtPrice } from "./api";

const uid = () => Math.random().toString(36).slice(2, 10);

// Coeur de l'assistant : messages + suggestions + capture de lead.
function ChatCore({ slug, brand, variant = "panel" }) {
  const greeting = { role: "assistant", content: `Bonjour 👋 Je suis l'assistant IA de ${brand}. Dites-moi ce que vous recherchez (secteur, budget, type de bien) et je vous propose les meilleures opportunités.` };
  const [messages, setMessages] = useState([greeting]);
  const [input, setInput] = useState("");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(uid());
  const [leadOpen, setLeadOpen] = useState(false);
  const [mode, setMode] = useState("lead"); // lead | book
  const [lead, setLead] = useState({ name: "", email: "", phone: "", date: "", time: "" });
  const [leadDone, setLeadDone] = useState(null);
  const [bookMsg, setBookMsg] = useState("");
  const [leadBusy, setLeadBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, matches, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next); setInput(""); setLoading(true);
    try {
      const r = await siteAIChat(slug, next.map((m) => ({ role: m.role, content: m.content })), sessionId);
      setMessages((m) => [...m, { role: "assistant", content: r.reply }]);
      if (Array.isArray(r.matches)) setMatches(r.matches);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, je rencontre un souci technique. Réessayez dans un instant." }]);
    }
    setLoading(false);
  };

  const submitLead = async () => {
    setLeadBusy(true);
    try {
      const summary = messages.filter((m) => m.role === "user").map((m) => m.content).join(" | ");
      if (mode === "book") {
        const r = await siteAIBook(slug, { ...lead, note: summary });
        setBookMsg(r.message || "Rendez-vous demandé !");
        setLeadDone(r.referral_link || "/inscription");
      } else {
        const r = await siteAILead(slug, { ...lead, summary });
        setLeadDone(r.referral_link || "/inscription");
      }
    } catch (_) {}
    setLeadBusy(false);
  };
  const openForm = (m) => { setMode(m); setLeadOpen(true); setLeadDone(null); setBookMsg(""); };

  return (
    <div className="flex flex-col h-full" data-testid="site-ai-chat">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] text-sm leading-relaxed px-3.5 py-2.5 rounded-2xl ${m.role === "user" ? "bg-accent text-white rounded-br-sm" : "bg-white/8 text-zinc-100 rounded-bl-sm"}`} data-testid={`chat-msg-${m.role}`}>{m.content}</div>
          </div>
        ))}
        {matches.length > 0 && (
          <div className="space-y-2 pt-1" data-testid="chat-matches">
            <p className="text-[11px] uppercase tracking-wider txt-accent flex items-center gap-1"><Sparkles className="w-3 h-3" /> Biens suggérés</p>
            {matches.map((l) => (
              <a key={l.slug} href={`/bien/${l.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`chat-match-${l.slug}`} className="flex items-center gap-3 border border-white/10 hover:bd-accent bg-white/5 rounded-xl overflow-hidden transition-colors">
                {l.cover ? <img src={l.cover} alt="" className="w-16 h-16 object-cover shrink-0" /> : <div className="w-16 h-16 bg-white/10 flex items-center justify-center shrink-0"><Home className="w-5 h-5 text-zinc-500" /></div>}
                <div className="py-2 pr-3 min-w-0">
                  <p className="text-sm text-white truncate">{l.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{[l.city, l.surface ? `${l.surface} m²` : null].filter(Boolean).join(" · ")}</p>
                  {l.price ? <p className="text-xs txt-accent">{fmtPrice(l.price)}</p> : null}
                </div>
              </a>
            ))}
          </div>
        )}
        {loading && <div className="flex justify-start"><div className="bg-white/8 rounded-2xl px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div></div>}
        <div ref={endRef} />
      </div>

      {leadOpen ? (
        <div className="border-t border-white/10 p-4 space-y-2" data-testid="chat-lead-form">
          {leadDone ? (
            <div className="text-center py-2" data-testid="chat-lead-done">
              <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-white">{mode === "book" ? (bookMsg || "Rendez-vous demandé !") : `Merci ! ${brand} vous tiendra informé des opportunités.`}</p>
              <a href={leadDone} className="btn-accent inline-flex items-center gap-2 mt-3 px-4 py-2 text-xs font-medium uppercase tracking-wider"><UserPlus className="w-4 h-4" /> Créer mon compte acquéreur</a>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-400">{mode === "book" ? "Choisissez un créneau et laissez vos coordonnées :" : "Laissez vos coordonnées pour être tenu informé :"}</p>
              <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Nom" data-testid="chat-lead-name" className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:bd-accent" />
              <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="E-mail" data-testid="chat-lead-email" className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:bd-accent" />
              <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Téléphone" data-testid="chat-lead-phone" className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:bd-accent" />
              {mode === "book" && (
                <div className="flex gap-2">
                  <input type="date" value={lead.date} onChange={(e) => setLead({ ...lead, date: e.target.value })} data-testid="chat-book-date" className="flex-1 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:bd-accent" />
                  <input type="time" value={lead.time} onChange={(e) => setLead({ ...lead, time: e.target.value })} data-testid="chat-book-time" className="w-28 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:bd-accent" />
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={submitLead} disabled={leadBusy || (!lead.name && !lead.email && !lead.phone) || (mode === "book" && !lead.date)} data-testid="chat-lead-submit" className="btn-accent flex-1 inline-flex items-center justify-center gap-2 py-2 text-xs font-medium uppercase tracking-wider disabled:opacity-50">{leadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {mode === "book" ? "Demander le RDV" : "Envoyer"}</button>
                <button onClick={() => setLeadOpen(false)} className="px-3 py-2 text-xs text-zinc-400 hover:text-white">Annuler</button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="border-t border-white/10 p-3">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button onClick={() => openForm("lead")} data-testid="chat-lead-open" className="inline-flex items-center justify-center gap-1.5 border bd-accent txt-accent hover:bg-white/5 rounded-lg py-2 text-xs font-medium uppercase tracking-wider transition-colors"><UserPlus className="w-4 h-4" /> Être informé</button>
            <button onClick={() => openForm("book")} data-testid="chat-book-open" className="inline-flex items-center justify-center gap-1.5 btn-accent rounded-lg py-2 text-xs font-medium uppercase tracking-wider"><Calendar className="w-4 h-4" /> Prendre RDV</button>
          </div>
          <div className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre recherche…" data-testid="chat-input" className="flex-1 bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-sm text-white outline-none focus:bd-accent" />
            <button onClick={send} disabled={loading || !input.trim()} data-testid="chat-send" className="btn-accent rounded-full p-2.5 disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// Bulle flottante (toutes les pages du site perso)
export function SiteAIChatBubble({ slug, brand }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-[#0b0b0e] border border-white/15 rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-testid="site-ai-bubble-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/[0.03]">
            <span className="text-sm font-medium text-white flex items-center gap-2"><Sparkles className="w-4 h-4 txt-accent" /> Assistant IA</span>
            <button onClick={() => setOpen(false)} data-testid="site-ai-bubble-close" className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 min-h-0"><ChatCore slug={slug} brand={brand} /></div>
        </div>
      )}
      <button onClick={() => setOpen((v) => !v)} data-testid="site-ai-bubble-toggle" className="fixed bottom-5 right-5 z-[60] btn-accent rounded-full w-14 h-14 flex items-center justify-center shadow-2xl transition-transform hover:scale-105 active:scale-95">
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>
    </>
  );
}

// Mode page plein écran (page « Assistant » du menu)
export function SiteAIChatPage({ slug, brand }) {
  return (
    <div className="pt-32 pb-20 px-5" data-testid="site-ai-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <p className="text-[11px] uppercase tracking-[0.25em] txt-accent mb-2">MARKETTE PLACE</p>
          <h1 className="text-3xl font-light text-white">Votre assistant immobilier IA</h1>
          <p className="text-sm text-zinc-400 mt-2">Décrivez votre projet, je trouve les biens qui vous correspondent parmi le portefeuille de {brand} et de son réseau.</p>
        </div>
        <div className="h-[62vh] min-h-[440px] bg-[#0b0b0e] border border-white/15 rounded-2xl overflow-hidden flex flex-col">
          <ChatCore slug={slug} brand={brand} />
        </div>
      </div>
    </div>
  );
}
